import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../components/Layout";
import api from "../../api";

const STATUS_META = {
  not_sent:     { label: "Not Sent",     bg: "#F1F5F9", color: "#94A3B8" },
  sent_to_jury: { label: "Sent to Jury", bg: "#FFFBEB", color: "#D97706" },
  scored:       { label: "Scored",       bg: "#ECFDF5", color: "#059669" },
  winner:       { label: "Winner 🏆",    bg: "#EEF2FF", color: "#4F46E5" },
  honorary:     { label: "Honorary",     bg: "#E0F7FA", color: "#0891B2" },
  not_selected: { label: "Not Selected", bg: "#FEF2F2", color: "#DC2626" },
};
const PAY_META = {
  paid:    { label: "Paid",    bg: "#dcfce7", color: "#16a34a" },
  free:    { label: "Free",    bg: "#eff6ff", color: "#2563eb" },
  pending: { label: "Pending", bg: "#fff7ed", color: "#ea580c" },
  failed:  { label: "Failed",  bg: "#fef2f2", color: "#dc2626" },
};

function ScoreRing({ score }) {
  const pct    = (score / 10) * 100;
  const radius = 36;
  const circ   = 2 * Math.PI * radius;
  const dash   = (pct / 100) * circ;
  const color  = score >= 8 ? "#10B981" : score >= 6 ? "#1D9AEC" : score >= 4 ? "#F59E0B" : "#EF4444";

  return (
    <div style={{ position: "relative", width: 88, height: 88 }}>
      <svg width={88} height={88} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={44} cy={44} r={radius} fill="none" stroke="#F1F5F9" strokeWidth={7} />
        <circle cx={44} cy={44} r={radius} fill="none" stroke={color} strokeWidth={7}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          style={{ transition: "stroke-dasharray 0.6s ease" }}
        />
      </svg>
      <div style={{
        position: "absolute", inset: 0,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      }}>
        <span style={{ fontWeight: 800, fontSize: 22, color, lineHeight: 1 }}>{score}</span>
        <span style={{ fontSize: 10, color: "#94A3B8", fontWeight: 500 }}>/10</span>
      </div>
    </div>
  );
}

/* ── Team research ───────────────────────────────────────────────────────
   Background the internal team gathers on a nominee before the award
   decision — a written note, a reference link, or an uploaded document.
   Everything added here is visible to jury members while they score.
------------------------------------------------------------------------ */
const RESEARCH_TYPES = [
  { key: "note",     label: "Note",     icon: "📝", hint: "What did the team find?" },
  { key: "link",     label: "Link",     icon: "🔗", hint: "https://…" },
  { key: "document", label: "Document", icon: "📄", hint: "Upload a PDF, doc or image" },
];

function ResearchManager({ submissionId, entries, onChange }) {
  const fileRef = useRef(null);
  const [open,      setOpen]      = useState(false);
  const [type,      setType]      = useState("note");
  const [title,     setTitle]     = useState("");
  const [content,   setContent]   = useState("");
  const [fileName,  setFileName]  = useState("");
  const [saving,    setSaving]    = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const reset = () => {
    setOpen(false); setType("note"); setTitle(""); setContent("");
    setFileName(""); setEditingId(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const startEdit = (r) => {
    setEditingId(r._id); setType(r.type); setTitle(r.title || "");
    setContent(r.content || ""); setFileName(r.fileName || ""); setOpen(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("documents", file);
      const res = await api.post("/form-submissions/research/upload", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const uploaded = res.data.data[0];
      setContent(uploaded.url);
      setFileName(uploaded.originalName);
      if (!title) setTitle(uploaded.originalName);
      toast.success("Document uploaded");
    } catch {
      toast.error("Upload failed — try again, or use a file under 10 MB");
    } finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!content.trim()) {
      toast.warning(type === "note" ? "Write the research note first" : "Add a URL first");
      return;
    }
    setSaving(true);
    try {
      const payload = { type, title, content, fileName };
      const res = editingId
        ? await api.put(`/form-submissions/${submissionId}/research/${editingId}`, payload)
        : await api.post(`/form-submissions/${submissionId}/research`, payload);
      onChange(res.data.data.researchNotes || []);
      toast.success(editingId ? "Research updated" : "Research added");
      reset();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not save the research entry");
    } finally { setSaving(false); }
  };

  const handleDelete = async (entryId) => {
    try {
      const res = await api.delete(`/form-submissions/${submissionId}/research/${entryId}`);
      onChange(res.data.data.researchNotes || []);
      toast.success("Research entry removed");
    } catch { toast.error("Could not remove the entry"); }
    setConfirmId(null);
  };

  const list = entries || [];
  const active = RESEARCH_TYPES.find(t => t.key === type);

  return (
    <div style={{ marginTop: 26, paddingTop: 22, borderTop: "1px solid #E2E8F0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 4, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#0F172A" }}>Team Research</h3>
        <span style={{ fontSize: 10, fontWeight: 700, background: "#EEF2FF", color: "#4F46E5", padding: "2px 8px", borderRadius: 20 }}>
          {list.length}
        </span>
        {!open && (
          <button onClick={() => setOpen(true)}
            style={{ marginLeft: "auto", background: "#4F46E5", color: "#fff", border: "none", borderRadius: 7, padding: "6px 13px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            + Add research
          </button>
        )}
      </div>
      <p style={{ margin: "0 0 16px", fontSize: 12.5, color: "#64748B" }}>
        Visible to jury members on their scoring page.
      </p>

      {/* Composer */}
      {open && (
        <div style={{ border: "1px solid #C7D2FE", background: "#F8FAFF", borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
            {RESEARCH_TYPES.map(t => {
              const on = type === t.key;
              return (
                <button key={t.key} onClick={() => { setType(t.key); setContent(""); setFileName(""); }} aria-pressed={on}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontFamily: "inherit",
                    background: on ? "#4F46E5" : "#fff", color: on ? "#fff" : "#475569",
                    border: `1.5px solid ${on ? "#4F46E5" : "#E2E8F0"}`,
                    padding: "6px 13px", borderRadius: 8, fontSize: 12.5, fontWeight: 600,
                  }}>
                  <span aria-hidden="true">{t.icon}</span> {t.label}
                </button>
              );
            })}
          </div>

          <label style={researchLbl} htmlFor="research-title">Title <span style={{ color: "#94A3B8", fontWeight: 500 }}>(optional)</span></label>
          <input id="research-title" value={title} onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Verified hospital accreditation" style={researchInp} />

          {type === "note" ? (
            <>
              <label style={{ ...researchLbl, marginTop: 12 }} htmlFor="research-body">Research note</label>
              <textarea id="research-body" rows={4} value={content} onChange={e => setContent(e.target.value)}
                placeholder={active.hint}
                style={{ ...researchInp, resize: "vertical", lineHeight: 1.55 }} />
            </>
          ) : type === "link" ? (
            <>
              <label style={{ ...researchLbl, marginTop: 12 }} htmlFor="research-url">URL</label>
              <input id="research-url" value={content} onChange={e => setContent(e.target.value)}
                placeholder={active.hint} style={researchInp} />
            </>
          ) : (
            <>
              <label style={{ ...researchLbl, marginTop: 12 }}>Document</label>
              <input ref={fileRef} type="file" onChange={handleUpload}
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xlsx,.csv"
                style={{ fontSize: 13, fontFamily: "inherit" }} />
              {uploading && <p style={{ margin: "8px 0 0", fontSize: 12.5, color: "#4F46E5" }}>Uploading…</p>}
              {content && !uploading && (
                <p style={{ margin: "8px 0 0", fontSize: 12.5, color: "#059669" }}>✓ {fileName || "File ready"}</p>
              )}
            </>
          )}

          <div style={{ display: "flex", gap: 9, marginTop: 16, flexWrap: "wrap" }}>
            <button onClick={handleSave} disabled={saving || uploading}
              style={{ background: saving || uploading ? "#C7D2FE" : "#4F46E5", color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: saving || uploading ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              {saving ? "Saving…" : editingId ? "Update entry" : "Add to research"}
            </button>
            <button onClick={reset}
              style={{ background: "#fff", color: "#475569", border: "1px solid #E2E8F0", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Entries */}
      {list.length === 0 && !open ? (
        <p style={{ fontSize: 13, color: "#94A3B8", margin: 0 }}>
          No research recorded yet. Anything added here reaches the jury with the nomination.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {list.map(r => (
            <div key={r._id} style={{ border: "1px solid #E2E8F0", borderRadius: 9, padding: "12px 14px", background: "#FBFCFD" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                <span aria-hidden="true">{RESEARCH_TYPES.find(t => t.key === r.type)?.icon || "📝"}</span>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: "#0F172A" }}>
                  {r.title || RESEARCH_TYPES.find(t => t.key === r.type)?.label}
                </span>
                <span style={{ fontSize: 11, color: "#94A3B8" }}>
                  {r.addedByName || "Team"}{r.addedAt ? ` · ${new Date(r.addedAt).toLocaleDateString("en-IN")}` : ""}
                </span>
                <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                  <button onClick={() => startEdit(r)} style={researchIconBtn("#475569")}>Edit</button>
                  <button onClick={() => setConfirmId(r._id)} style={researchIconBtn("#DC2626")}>Remove</button>
                </span>
              </div>

              {r.type === "note" ? (
                <p style={{ margin: 0, fontSize: 13.5, color: "#1E293B", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{r.content}</p>
              ) : (
                <a href={r.content} target="_blank" rel="noreferrer"
                  style={{ fontSize: 13.5, color: "#1D9AEC", fontWeight: 600, textDecoration: "none", wordBreak: "break-all" }}>
                  {r.type === "document" ? (r.fileName || "Open document") : r.content} ↗
                </a>
              )}

              {confirmId === r._id && (
                <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12.5, color: "#92400E" }}>Remove this entry? The jury will no longer see it.</span>
                  <button onClick={() => handleDelete(r._id)} style={researchIconBtn("#DC2626")}>Yes, remove</button>
                  <button onClick={() => setConfirmId(null)} style={researchIconBtn("#475569")}>Keep</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Append-only trail of who moved this nomination through the workflow
function StatusHistory({ entries }) {
  const list = entries || [];
  if (list.length === 0) return null;
  const label = (s) => (STATUS_META[s]?.label || s || "—").replace(" 🏆", "");

  return (
    <div style={card}>
      <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: "#0F172A" }}>Decision Trail</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {[...list].reverse().map(h => (
          <div key={h._id} style={{ borderLeft: "2px solid #E2E8F0", paddingLeft: 12 }}>
            <p style={{ margin: 0, fontSize: 13, color: "#0F172A", fontWeight: 600 }}>
              {label(h.from)} → {label(h.to)}
            </p>
            <p style={{ margin: "2px 0 0", fontSize: 11.5, color: "#94A3B8" }}>
              {h.actorName || "Admin"}
              {h.at ? ` · ${new Date(h.at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} ${new Date(h.at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : ""}
            </p>
            {h.remarks && (
              <p style={{ margin: "5px 0 0", fontSize: 12.5, color: "#64748B", fontStyle: "italic", lineHeight: 1.5 }}>"{h.remarks}"</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const researchLbl = { display: "block", fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 };
const researchInp = { width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13.5, boxSizing: "border-box", outline: "none", fontFamily: "inherit", color: "#0F172A", background: "#fff" };
const researchIconBtn = (color) => ({ background: "none", border: "none", cursor: "pointer", color, fontSize: 12, fontWeight: 600, padding: "2px 4px", fontFamily: "inherit" });

export default function AdminSubmissionPage() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [scores,     setScores]     = useState([]);
  const [loadErr,    setLoadErr]    = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/form-submissions/${submissionId}`),
      api.get(`/jury/scores/submission/${submissionId}`),
    ]).then(([subRes, scoreRes]) => {
      setSubmission(subRes.data.data);
      setScores(scoreRes.data.data || []);
    }).catch(() => setLoadErr(true));
  }, [submissionId]);

  if (loadErr) return (
    <Layout title="Error">
      <button onClick={() => navigate(-1)} style={{ marginBottom: 20, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, padding: "7px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#475569" }}>
        ← Back
      </button>
      <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 12, padding: 32, textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 10 }}>⚠️</div>
        <p style={{ fontWeight: 700, color: "#DC2626", margin: "0 0 6px" }}>Failed to load submission</p>
        <p style={{ color: "#94A3B8", fontSize: 13, margin: 0 }}>The submission may have been deleted or you don't have access.</p>
      </div>
    </Layout>
  );

  if (!submission) return (
    <Layout title="Loading…">
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {[1, 2].map(i => (
          <div key={i} style={card}>
            <div className="skeleton" style={{ height: 14, width: "40%", marginBottom: 10 }} />
            <div className="skeleton" style={{ height: 10, width: "60%", marginBottom: 8 }} />
            <div className="skeleton" style={{ height: 10, width: "50%" }} />
          </div>
        ))}
      </div>
    </Layout>
  );

  const INTERNAL_KEYS = new Set([
    "leadsource","leadtype","crmeventid","leadownerid","eventid","formid",
    "leadSource","leadType","crmEventId","leadOwnerId","eventId","formId",
    "zohoStatus","zohoLeadId","salutation",
  ]);
  const data    = submission.submissionData || {};
  const fields  = Object.entries(data).filter(([k]) => {
    if (k.startsWith("_")) return false;
    const norm = k.toLowerCase().replace(/[\s_]/g, "");
    return !INTERNAL_KEYS.has(k) && !INTERNAL_KEYS.has(norm);
  });
  const statusM = STATUS_META[submission.juryStatus] || STATUS_META.not_sent;
  const avgScore = submission.averageScore;

  return (
    <Layout title="Submission Detail">
      <button
        onClick={() => navigate(-1)}
        style={{
          marginBottom: 22, background: "#fff", border: "1px solid #E2E8F0",
          borderRadius: 8, padding: "7px 16px", cursor: "pointer",
          fontSize: 13, fontWeight: 600, color: "#475569",
          display: "inline-flex", alignItems: "center", gap: 6,
          boxShadow: "var(--shadow-xs)", transition: "box-shadow 0.15s",
        }}
      >
        ← Back
      </button>

      <div className="voh-split" style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 20, alignItems: "start" }}>

        {/* Left — nomination data */}
        <div style={card}>
          {/* Header badges */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
            {submission.eventFormId?.formName && (
              <span style={badge("blue")}>{submission.eventFormId.formName}</span>
            )}
            {submission.nominationCategory && (
              <span style={badge("purple")}>{submission.nominationCategory}</span>
            )}
            {submission.nominationSubCategory && (
              <span style={badge("purple")}>{submission.nominationSubCategory}</span>
            )}
            <span style={{
              display: "inline-block", padding: "4px 12px", borderRadius: 20,
              fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
              background: statusM.bg, color: statusM.color,
            }}>{statusM.label}</span>
            {(() => {
              const pm = PAY_META[submission.paymentStatus];
              if (!pm) return null;
              return <span style={{ display: "inline-block", padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", background: pm.bg, color: pm.color }}>{pm.label}</span>;
            })()}
          </div>

          <h3 style={{ margin: "0 0 18px", fontSize: 14, fontWeight: 700, color: "#0F172A" }}>
            Nomination Details
          </h3>

          {/* Fields grid */}
          <div className="voh-fields" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 24px" }}>
            {fields.map(([key, val]) => (
              <div key={key} style={{ borderBottom: "1px solid #F8FAFC", paddingBottom: 12 }}>
                <p style={{ fontSize: 10.5, color: "#94A3B8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 4px" }}>
                  {key.replace(/([A-Z])/g, " $1").trim()}
                </p>
                <p style={{ fontSize: 13.5, color: "#1E293B", margin: 0, wordBreak: "break-word", lineHeight: 1.5 }}>
                  {typeof val === "string" && val.startsWith("http") ? (
                    <a href={val} target="_blank" rel="noreferrer"
                      style={{ color: "#1D9AEC", fontWeight: 600, textDecoration: "none" }}>
                      View document ↗
                    </a>
                  ) : String(val)}
                </p>
              </div>
            ))}
          </div>

          <ResearchManager
            submissionId={submissionId}
            entries={submission.researchNotes}
            onChange={(researchNotes) => setSubmission(s => ({ ...s, researchNotes }))}
          />

          {/* Final remarks */}
          {submission.finalRemarks && (
            <div style={{
              marginTop: 20, padding: "14px 16px",
              background: "#FFFBEB", border: "1px solid #FDE68A",
              borderRadius: 9, fontSize: 13, color: "#92400E",
              lineHeight: 1.6,
            }}>
              <strong>Final Remarks:</strong> {submission.finalRemarks}
            </div>
          )}
        </div>

        {/* Right — jury scores */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Average score card */}
          <div style={{
            ...card,
            background: avgScore != null
              ? "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)"
              : "#fff",
            textAlign: "center", padding: "28px 20px",
          }}>
            {avgScore != null ? (
              <>
                <p style={{ fontSize: 11, fontWeight: 700, color: "#64748B", letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 16px" }}>
                  AVERAGE SCORE
                </p>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                  <ScoreRing score={avgScore} />
                </div>
                <p style={{ fontSize: 12, color: "#64748B", margin: 0 }}>
                  Based on {scores.length} jury {scores.length === 1 ? "score" : "scores"}
                </p>
              </>
            ) : (
              <>
                <div style={{ fontSize: 36, marginBottom: 10 }}>⏳</div>
                <p style={{ fontSize: 13, color: "#94A3B8", margin: 0, fontWeight: 500 }}>
                  No scores yet
                </p>
              </>
            )}
          </div>

          {/* Individual scores */}
          <div style={card}>
            <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: "#0F172A" }}>
              Jury Scores
            </h3>

            {scores.length === 0 ? (
              <p style={{ fontSize: 13, color: "#94A3B8", margin: 0 }}>No scores submitted yet.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {scores.map(s => {
                  const scoreColor = s.score >= 8 ? "#10B981" : s.score >= 6 ? "#1D9AEC" : s.score >= 4 ? "#F59E0B" : "#EF4444";
                  return (
                    <div key={s._id} style={{
                      display: "flex", alignItems: "flex-start", gap: 12,
                      paddingBottom: 14, borderBottom: "1px solid #F8FAFC",
                    }}>
                      {/* Avatar */}
                      <div style={{
                        width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                        background: "linear-gradient(135deg, #6366F1, #1D9AEC)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontWeight: 700, fontSize: 14, color: "#fff",
                      }}>
                        {(s.juryId?.name || "J")[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <span style={{ fontWeight: 600, fontSize: 13.5, color: "#0F172A" }}>{s.juryId?.name}</span>
                            <p style={{ margin: "2px 0 0", fontSize: 11, color: "#94A3B8" }}>{s.juryId?.email}</p>
                          </div>
                          <div style={{
                            width: 38, height: 38, borderRadius: "50%",
                            background: `${scoreColor}18`,
                            border: `2px solid ${scoreColor}`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontWeight: 800, fontSize: 15, color: scoreColor,
                          }}>{s.score}</div>
                        </div>
                        {s.remarks && (
                          <p style={{ margin: "8px 0 0", fontSize: 12.5, color: "#64748B", fontStyle: "italic", lineHeight: 1.5 }}>
                            "{s.remarks}"
                          </p>
                        )}
                        <p style={{ margin: "4px 0 0", fontSize: 11, color: "#CBD5E1" }}>
                          {new Date(s.scoredAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                          {" · "}
                          {new Date(s.scoredAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <StatusHistory entries={submission.statusHistory} />
        </div>
      </div>
    </Layout>
  );
}

const card = {
  background: "#fff", borderRadius: 12, padding: 24,
  boxShadow: "var(--shadow-sm)", border: "1px solid #E2E8F0",
};
const badge = (color) => ({
  display: "inline-block", padding: "4px 12px", borderRadius: 20,
  fontSize: 11, fontWeight: 600,
  background: color === "blue" ? "#EFF8FF" : "#F5F3FF",
  color:      color === "blue" ? "#0369A1" : "#6D28D9",
  border: `1px solid ${color === "blue" ? "#BFDBFE" : "#DDD6FE"}`,
});
